import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { createClassroom } from "../services/classroom";
import { listFrequencies } from "../services/teacher";
import { listActiveStudents } from "../services/student";
import TransferList from "./shared/TransferList";
import WizardSteps from "./shared/WizardSteps";
import { levelKey } from "../utils/classesLabels";

const TOTAL_STEPS = 3;
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
const SHIFT_OPTIONS = ["Matutino", "Vespertino", "Diurno", "Nocturno"];

const initialState = {
  className: "",
  code: "",
  academicYear: currentYear,
  educationLevel: "",
  shift: "Diurno",
  maxStudents: 25,
};

// Step 2 used to allocate teachers here; teacher allocation now happens only
// from ListaTurmasLayer's "Editar" modal after the turma exists. This step
// allocates students instead, using the same dual-list transfer UI (and
// shared TransferList component) as AlocarEstudantesLayer.
const CriarTurmaLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [frequencies, setFrequencies] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [registrations, setRegistrations] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [allocatedStudentIds, setAllocatedStudentIds] = useState([]);
  const [searchLeft, setSearchLeft] = useState("");
  const [searchRight, setSearchRight] = useState("");
  const [selectedLeft, setSelectedLeft] = useState([]);
  const [selectedRight, setSelectedRight] = useState([]);

  const stepLabels = [t("turma.wizard.step.data"), t("turma.wizard.step.students"), t("turma.wizard.step.confirm")];

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
  }, []);

  useEffect(() => {
    setLoadingStudents(true);
    listActiveStudents(form.academicYear)
      .then((resp) => setRegistrations(resp.data || []))
      .catch(() => setRegistrations([]))
      .finally(() => setLoadingStudents(false));
  }, [form.academicYear]);

  const genderLabel = (value) => {
    const normalized = (value || "").toString().trim().toUpperCase();
    if (normalized.startsWith("M")) return t("global.male");
    if (normalized.startsWith("F")) return t("global.female");
    return "-";
  };

  const pool = useMemo(() => {
    const map = new Map();
    registrations
      .filter(
        (r) =>
          String(r.corseId) === String(form.educationLevel) ||
          String(r.student?.level) === String(form.educationLevel),
      )
      .forEach((r) => {
        map.set(r.studentId, {
          id: r.studentId,
          name: r.student?.name || "",
          code: r.student?.code || "",
          gender: r.student?.sex || "",
        });
      });
    return map;
  }, [registrations, form.educationLevel]);

  const allItems = useMemo(() => Array.from(pool.values()), [pool]);

  const unallocatedStudents = useMemo(() => {
    const term = searchLeft.trim().toLowerCase();
    return allItems.filter(
      (it) => !allocatedStudentIds.includes(it.id) && (!term || it.name.toLowerCase().includes(term)),
    );
  }, [allItems, allocatedStudentIds, searchLeft]);

  const allocatedStudents = useMemo(() => {
    const term = searchRight.trim().toLowerCase();
    return allItems.filter(
      (it) => allocatedStudentIds.includes(it.id) && (!term || it.name.toLowerCase().includes(term)),
    );
  }, [allItems, allocatedStudentIds, searchRight]);

  const toggleLeft = (id) =>
    setSelectedLeft((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const toggleRight = (id) =>
    setSelectedRight((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const selectAllLeft = () => {
    const ids = unallocatedStudents.map((it) => it.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedLeft.includes(id));
    setSelectedLeft(allSelected ? [] : ids);
  };

  const selectAllRight = () => {
    const ids = allocatedStudents.map((it) => it.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedRight.includes(id));
    setSelectedRight(allSelected ? [] : ids);
  };

  const moveRight = () => {
    setAllocatedStudentIds((cur) => Array.from(new Set([...cur, ...selectedLeft])));
    setSelectedLeft([]);
  };

  const moveLeft = () => {
    setAllocatedStudentIds((cur) => cur.filter((id) => !selectedRight.includes(id)));
    setSelectedRight([]);
  };

  const overCapacity = Number(form.maxStudents) > 0 && allocatedStudentIds.length > Number(form.maxStudents);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleClassNameChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, className: value, code: value.replace(/\s+/g, "").toUpperCase() }));
  };

  const nextStep = () => {
    if (currentStep === 1 && (!form.className || !form.academicYear || !form.educationLevel || !form.shift)) {
      toast.error(t("turma.error.requiredFields"));
      return;
    }
    if (currentStep === 2 && overCapacity) {
      toast.error(t("turma.allocate.error.capacity"));
      return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const restart = () => {
    setForm(initialState);
    setAllocatedStudentIds([]);
    setSelectedLeft([]);
    setSelectedRight([]);
    setCurrentStep(1);
    setDone(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const selectedStudents = allocatedStudentIds.map((id) => {
        const item = pool.get(id);
        return { id, name: item?.name || "", code: item?.code || "" };
      });
      await createClassroom({
        className: form.className,
        code: form.code,
        academicYear: Number(form.academicYear),
        educationLevel: form.educationLevel,
        shift: form.shift,
        maxStudents: Number(form.maxStudents) || 1,
        students: selectedStudents,
      });
      setDone(true);
    } catch (err) {
      toast.error(t("turma.error.create"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("turma.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("turma.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("turma.field.className")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="mdi:google-classroom" />
                        </span>
                        <input type='text' className='form-control' value={form.className} onChange={handleClassNameChange} required />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("turma.field.code")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:hashtag-square-outline" />
                        </span>
                        <input type='text' className='form-control' value={form.code} onChange={setField("code")} />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("turma.field.academicYear")}</label>
                      <select className='form-select' value={form.academicYear} onChange={setField("academicYear")}>
                        {YEAR_OPTIONS.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t(levelKey("turma.field.educationLevel"))}</label>
                      <select className='form-select' value={form.educationLevel} onChange={setField("educationLevel")} required>
                        <option value=''>{t(levelKey("turma.field.educationLevel.placeholder"))}</option>
                        {frequencies.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.description}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("turma.field.shift")}</label>
                      <select className='form-select' value={form.shift} onChange={setField("shift")}>
                        {SHIFT_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("turma.field.maxStudents")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="mdi:google-classroom" />
                        </span>
                        <input type='number' min='1' className='form-control' value={form.maxStudents} onChange={setField("maxStudents")} />
                      </div>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end mt-24'>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  {overCapacity && (
                    <div className='alert alert-danger d-flex align-items-center gap-2 mb-16'>
                      <Icon icon='mdi:alert-circle-outline' />
                      {t("turma.allocate.error.capacity")}
                    </div>
                  )}
                  <TransferList
                    leftTitle={t("turma.allocate.unallocated")}
                    rightTitle={t("turma.allocate.allocated")}
                    leftItems={unallocatedStudents}
                    rightItems={allocatedStudents}
                    selectedLeft={selectedLeft}
                    selectedRight={selectedRight}
                    onToggleLeft={toggleLeft}
                    onToggleRight={toggleRight}
                    onSelectAllLeft={selectAllLeft}
                    onSelectAllRight={selectAllRight}
                    onMoveRight={moveRight}
                    onMoveLeft={moveLeft}
                    searchLeft={searchLeft}
                    onSearchLeftChange={setSearchLeft}
                    searchRight={searchRight}
                    onSearchRightChange={setSearchRight}
                    loading={loadingStudents}
                    loadingText={t("global.loading")}
                    emptyLeftText={t("turma.allocate.empty.unallocated")}
                    emptyRightText={t("turma.allocate.empty.allocated")}
                    selectAllLabel={t("turma.allocate.selectAll")}
                    searchPlaceholder={t("turma.allocate.search.placeholder")}
                    renderItemMeta={(item) => `${item.code || "-"} · ${genderLabel(item.gender)}`}
                  />
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button type='button' className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32' onClick={prevStep}>
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 3 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("turma.confirm.description")}</div>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <ul className='list-unstyled d-flex flex-column gap-8'>
                        <li>
                          <strong>{t("turma.field.className")}:</strong> {form.className}
                        </li>
                        <li>
                          <strong>{t("turma.field.academicYear")}:</strong> {form.academicYear}
                        </li>
                        <li>
                          <strong>{t(levelKey("turma.field.educationLevel"))}:</strong>{" "}
                          {frequencies.find((f) => f.id === form.educationLevel)?.description}
                        </li>
                        <li>
                          <strong>{t("turma.field.shift")}:</strong> {form.shift}
                        </li>
                        <li>
                          <strong>{t("turma.field.maxStudents")}:</strong> {form.maxStudents}
                        </li>
                      </ul>
                    </div>
                    <div className='col-sm-6'>
                      <strong>{t("turma.field.students")}:</strong>{" "}
                      {t("turma.hint.selectedCount", { count: allocatedStudentIds.length })}
                      {allocatedStudentIds.length > 0 && (
                        <p className='text-secondary-light text-sm mt-8 mb-0'>
                          {allocatedStudentIds.map((id) => pool.get(id)?.name).filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={prevStep}
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button
                      type='button'
                      className='form-wizard-submit btn btn-primary-600 px-32'
                      onClick={handleSubmit}
                      disabled={submitting}
                    >
                      {submitting ? t("global.loading") : <><i className='ri-check-line' /> {t("global.confirm")}</>}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>{t("turma.success.created")}</h6>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={restart}>
                      {t("turma.action.createAnother")}
                    </button>
                    <button type='button' className='btn btn-primary-600 px-32' onClick={() => navigate("/turmas/lista")}>
                      <i className='ri-arrow-left-line' /> {t("turma.breadcrumb.list")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CriarTurmaLayer;
